// src/pages/Help.js
//
// Chunk 9a — restyled and FAQ rewritten for the new IA.
//
// Old refs scrubbed: "Transactions" page → "Log", "Bills" page → "Plan",
// "Dashboard" → "This Month". Setup wizard FAQ updated to describe the
// welcome strip in This Month, not the deleted modal.
//
import React, { useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { theme, atoms } from '../lib/theme'

const FAQ = [
  {
    category: 'Getting started',
    icon: '🚀',
    questions: [
      {
        q: 'How do I set up Merq Vault?',
        a: `When you first sign up you'll see a "Welcome" guide on This Month with three soft prompts: Add your income, Set spending limits, and Log a transaction. Each one takes about a minute. The guide auto-dismisses once you've done all three. There's no forced wizard — you can skip it and the guide will stay until you finish, or click Skip to dismiss it forever.`,
      },
      {
        q: 'What should I do in my first week?',
        a: `Three things. First, log every transaction as you spend — go to Log, tap the + button, enter the amount, pick a category, and save. It takes about 10 seconds. Second, check This Month every couple of days to see where you stand against your spending limits. Third, at the end of the month, head to Net Worth → Update balances and enter your current account balances.`,
      },
      {
        q: 'Do I need to connect my bank account?',
        a: `No — and you never will. Merq Vault is manual by design. You enter your own transactions, and you update your account balances yourself once a month. This means your bank credentials never leave your bank, and your financial data is never shared with third parties. It takes a little more effort, but it's also more private and more intentional.`,
      },
      {
        q: 'How do I add Merq Vault to my phone home screen?',
        a: `On iPhone: open merqvault.com in Safari, tap the Share button at the bottom, then tap "Add to Home Screen." On Android: open merqvault.com in Chrome, tap the three-dot menu, then tap "Add to Home Screen." Once added, Merq Vault opens full screen just like a native app — no browser bar, and Face ID login is supported.`,
      },
    ],
  },
  {
    category: 'This Month',
    icon: '🏠',
    questions: [
      {
        q: 'What does the "Left to spend" number mean?',
        a: `It's everything you've allocated for this month minus everything you've spent so far. If your plan adds up to $4,500 and you've spent $1,200, "Left to spend" shows $3,300. This is the headline number you check every day to know if you're on track.`,
      },
      {
        q: 'What is "Coming up"?',
        a: `Bills with due dates in the next ~30 days that you haven't paid yet this month. Each card shows the bill name, amount, days until due, and a "Mark paid" button. Tap Mark paid → enter the amount + date + payment method → it logs a transaction with a link back to the bill template, and the card flips to "✓ Paid".`,
      },
      {
        q: 'What is "On track this month"?',
        a: `A snapshot of your two biggest moves: your active savings goal and your active debt payoff. Each shows current progress versus your monthly target, so you can see at a glance whether you're hitting both.`,
      },
    ],
  },
  {
    category: 'Plan',
    icon: '📊',
    questions: [
      {
        q: 'What goes on the Plan page?',
        a: `Plan is where you set up your month: income sources, recurring bills, savings goals, debt payoff plans, and spending categories with limits. Anything that's about deciding ahead of time how money should flow goes here. Once a month is enough — Merq Vault auto-creates next month's plan from this month's structure.`,
      },
      {
        q: 'How do I change a category limit?',
        a: `Go to Plan, find the category, and tap the limit amount. Type a new number and save. You can also add or remove categories using the + button next to the section header.`,
      },
      {
        q: 'What are subcategories?',
        a: `Subcategories let you break a broad category into more detail. "Food" might have subcategories for Groceries, Dining out, and Coffee. The parent total is the sum of its subcategories. Tap the + on any parent category to add a subcategory.`,
      },
      {
        q: 'Does my plan reset every month?',
        a: `Yes. Each month gets its own plan. At the start of a new month, Merq Vault automatically creates a new plan by copying your categories, limits, and bills from the previous month. Your spending resets to zero — you start fresh with the same structure.`,
      },
      {
        q: 'Can I look at past months?',
        a: `Yes. Use the chevron arrows at the top of Plan to navigate. Past months are read-only — you can see what was set up but not edit. For a printable summary, go to Reports.`,
      },
      {
        q: 'How do I add a bill that only happens once?',
        a: `On Plan, click + Add bill, then change Frequency to "One time." The "End date" field disappears and "Due day of month" becomes "Day of month." Save. The bill will show on This Month the month it's due, and won't recur after that.`,
      },
    ],
  },
  {
    category: 'Log',
    icon: '💳',
    questions: [
      {
        q: 'How do I log a transaction?',
        a: `Tap the + button on Log (or the floating + on This Month). Enter the amount, pick a category, optionally add merchant name and payment method. Save. That's it — about 10 seconds.`,
      },
      {
        q: 'What is the merchant autocomplete?',
        a: `When you type a merchant name you've used before, Merq Vault suggests it from your history and pre-fills the category that merchant was last assigned to. So if you always put "Whole Foods" under Groceries, it'll pre-select that for you next time.`,
      },
      {
        q: 'How do I mark a transaction as a business expense?',
        a: `When adding or editing, switch the Tax type segment to "Business" and add a memo describing the purpose. The transaction gets flagged for the year-end Business Expense Report PDF, available on Reports.`,
      },
      {
        q: 'What is the receipt scanner?',
        a: `Tap the camera icon on Log (or the scanner FAB on mobile) to scan a receipt. Merq Vault uses AI to extract the amount, merchant name, and date and pre-fills the transaction form. You still review and confirm before saving. Receipts are stored privately in your account and shown on the transaction row in Log.`,
      },
      {
        q: 'How do I edit or delete a transaction?',
        a: `Tap any row on Log to open the edit sheet. Change any field, save. To delete, tap the ⋯ menu on the row → Delete. To bulk-delete or recategorize, use the checkboxes on each row to select multiple, then use the action bar at the bottom.`,
      },
      {
        q: 'Can I export my transactions?',
        a: `Yes — on Log, tap "Export CSV" in the header to export everything visible. Or select rows with the checkboxes and tap "Export" in the bulk action bar. CSVs include date, merchant, category, amount, payment method, business flag, memo, and who added it.`,
      },
      {
        q: 'What does "matched bill" mean on a row?',
        a: `When you mark a bill paid, the resulting transaction gets a link back to the bill template. The Log row shows "matched bill · Rent" (or whichever bill name) so you know it was a recurring payment, not a one-off. Helpful when reviewing history.`,
      },
    ],
  },
  {
    category: 'Net Worth',
    icon: '🏦',
    questions: [
      {
        q: 'How does net worth tracking work?',
        a: `Net worth is your total assets minus your total liabilities. You add your accounts once (checking, savings, investments, home value, loans, credit cards) and then update the balances manually once a month. Merq Vault calculates net worth automatically from whatever balances you enter.`,
      },
      {
        q: "Why doesn't my loan balance update automatically when I make a payment?",
        a: `Merq Vault doesn't connect to your bank or loan servicer, so nothing updates automatically. When you make a loan payment, part goes to principal and part goes to interest — and only your bank statement knows the exact remaining balance. Log into your bank or loan servicer, find the current balance, and enter it on Net Worth → Update balances.`,
      },
      {
        q: 'How often should I update my balances?',
        a: `Once a month is the recommended cadence. Pick a day — the 1st or last day of the month works well — and spend 5 minutes going through each account. This gives you an accurate monthly snapshot of net worth over time, which is what feeds the trend sparkline above the hero number.`,
      },
      {
        q: 'What does the sparkline above my net worth show?',
        a: `The last 6 months of your net worth, plotted from your balance update history. The pill below it ("+$X over 6 months" or "−$X over 6 months") shows your trajectory. The sparkline only appears once you've got at least 2 months of distinct balance data — otherwise there's nothing meaningful to plot.`,
      },
      {
        q: "What's the difference between My Accounts and Update Balances?",
        a: `My Accounts is where you set up your accounts once — add their names, types, and institutions. You only do this when you open a new account or close an old one. Update Balances is where you go each month to enter the current balance for each account. Think of My Accounts as the registry and Update Balances as the monthly habit.`,
      },
      {
        q: 'Can my partner see my net worth?',
        a: `Yes — if you're connected to a partner, the Net Worth page shows your combined net worth including both your accounts and theirs. Each account row shows who it belongs to so you can see the breakdown.`,
      },
    ],
  },
  {
    category: 'Bills',
    icon: '📅',
    questions: [
      {
        q: "What's the difference between a bill and a transaction?",
        a: `A bill is a recurring payment template you expect every month — rent, internet, car insurance. Bill templates live on Plan. When you actually pay a bill, you log a transaction (either by tapping "Mark paid" on This Month, or by adding it manually on Log). Merq Vault links the transaction to the bill so the bill flips to "✓ Paid" for that month.`,
      },
      {
        q: 'How do I mark a bill as paid?',
        a: `Two ways. On This Month under Coming up, tap "Mark paid" on the bill card → confirm amount + date + payment method → done. Or log a transaction on Log with a similar merchant name and amount — Merq Vault detects the match and asks "This looks like your Rent payment of $3,750. Mark it as paid?" Either way, the bill gets linked.`,
      },
      {
        q: 'When do I get reminders?',
        a: `This Month shows upcoming bills under "Coming up" — bills with due dates in the next ~30 days. Bills due within 4 days are highlighted in orange. You can set a reminder window per bill on Plan — the default is 3 days before the due date.`,
      },
      {
        q: 'How do I add a bill quickly?',
        a: `Two ways. On Plan, click + Add bill and fill in the form. Or — fastest — tap the green "Scan a receipt to fill this in" button at the top of the bill form. Take a photo of the bill or invoice; the AI extracts the amount, name, and date and pre-fills the form. You confirm and save.`,
      },
    ],
  },
  {
    category: 'Reports',
    icon: '📈',
    questions: [
      {
        q: 'What can I generate on Reports?',
        a: `Two kinds of reports. The Financial Summary picks one or more months and produces a printable on-screen review plus a downloadable PDF showing income, spending by category, budget variance, and savings rate. The Business Expense Report is a separate PDF for a single month with all your business-tagged transactions and receipt images attached — built for accountants and tax records.`,
      },
      {
        q: 'How do I print a "month review"?',
        a: `On the Reports page, the top card has a "Print this month's review" button. One click and it generates the current month's summary and pops up your browser's print dialog. There's also a "Print month review" button at the top of Log that does the same thing.`,
      },
      {
        q: 'Where is the business expense report?',
        a: `On Reports, scroll to the second card titled "Business expense report." Pick a month and year → see the count of expenses, receipts, and total → click Download PDF. Receipt images are attached as separate pages at the end of the PDF.`,
      },
    ],
  },
  {
    category: 'Partner sharing',
    icon: '👫',
    questions: [
      {
        q: 'How do I invite my partner?',
        a: `Go to Settings → Partner and enter your partner's email address. They'll receive an invite link. Once they create their account and accept the invite, you'll be connected and can see each other's plan, net worth, transactions, and bills.`,
      },
      {
        q: 'Does my partner need to pay separately?',
        a: `No — one subscription covers both of you. Whoever subscribes covers the partnership. Your partner gets full access to all features at no extra cost.`,
      },
      {
        q: "Can we see each other's transactions?",
        a: `Yes — on Log, you'll see transactions from both of you, with a name label showing who added each one. You can filter by person using the "Person" chip and group by person using the "By person" view toggle.`,
      },
      {
        q: 'What if we break up or I want to disconnect my partner?',
        a: `Go to Settings → Partner and tap "Disconnect partner." This immediately removes the shared view. Each person keeps their own account and data — nothing is deleted, you just stop seeing each other's information.`,
      },
    ],
  },
  {
    category: 'Account & security',
    icon: '🔒',
    questions: [
      {
        q: 'How do I enable Face ID or Touch ID login?',
        a: `The first time you log in with your password on a new device, Merq Vault asks if you want to enable biometric login. Tap Enable and follow the prompt. After setup, you'll see a biometric button on the login screen — one tap and you're in.`,
      },
      {
        q: 'Is my financial data secure?',
        a: `Yes. Your data is stored in Supabase with row-level security — meaning each user can only access their own data. We never connect to your bank, never share your data with third parties, and never sell your information. Your password is hashed and never stored in plain text.`,
      },
      {
        q: 'How do I change my password?',
        a: `Go to the login page and tap "Forgot password?" Enter your email and you'll receive a reset link. You can also change your password from Settings → Profile.`,
      },
      {
        q: 'What happens to my data if I cancel?',
        a: `Your account and data remain intact. You just lose access to the app features until you resubscribe. We don't delete your data when you cancel — if you come back, everything will be exactly as you left it.`,
      },
    ],
  },
  {
    category: 'Subscription & billing',
    icon: '💰',
    questions: [
      {
        q: 'How much does Merq Vault cost?',
        a: `Merq Vault is $59 per year or $7 per month. One subscription covers you and your partner — no per-seat pricing. You get a free trial with full access to all features, no credit card required.`,
      },
      {
        q: 'What happens when my trial ends?',
        a: `Nothing happens automatically — we don't auto-charge you. When your trial ends, you'll be prompted to choose a plan to continue. If you don't subscribe, you'll lose access to adding new data but can still view your existing data.`,
      },
      {
        q: 'Can I get a refund?',
        a: `If you're unhappy within the first 30 days of a paid subscription, contact us at hello@getmerq.com and we'll make it right. We'd rather refund you than have an unhappy customer.`,
      },
    ],
  },
]

export default function Help() {
  const ctx = useOutletContext() || {}
  const isMobile = !!ctx.isMobile
  const [search, setSearch] = useState('')
  const [openItems, setOpenItems] = useState({})

  const toggleItem = (key) => {
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const filteredFAQ = FAQ.map(cat => ({
    ...cat,
    questions: cat.questions.filter(q =>
      !search ||
      q.q.toLowerCase().includes(search.toLowerCase()) ||
      q.a.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(cat => cat.questions.length > 0)

  const totalResults = filteredFAQ.reduce((sum, cat) => sum + cat.questions.length, 0)

  return (
    <div style={isMobile ? styles.pageMobile : styles.page}>

      {/* Header */}
      <div style={isMobile ? styles.headerMobile : styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.pageTitle}>Help</h1>
          <div style={atoms.pageMeta}>
            <span>Search the FAQ or get in touch</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div style={styles.searchWrap}>
        <input
          style={styles.searchInput}
          type="text"
          placeholder="Search for anything — e.g. 'how do I add a transaction'"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          aria-label="Search FAQ"
        />
        {search && (
          <button
            type="button" className="btn-compact"
            style={styles.clearBtn}
            onClick={() => setSearch('')}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {search && (
        <div style={styles.resultCount}>
          {totalResults === 0
            ? <>No results found for <strong style={{ color: theme.colors.plum }}>"{search}"</strong></>
            : <>{totalResults} result{totalResults !== 1 ? 's' : ''} for <strong style={{ color: theme.colors.plum }}>"{search}"</strong></>}
        </div>
      )}

      {totalResults === 0 && search && (
        <div style={styles.emptySearch}>
          <div style={styles.emptyTitle}>No matches</div>
          <div style={styles.emptyText}>
            Try a different search, or <a href="mailto:hello@getmerq.com" style={styles.emptyLink}>email us</a> and we'll add an answer for you.
          </div>
        </div>
      )}

      {/* FAQ sections */}
      {filteredFAQ.map(cat => (
        <div key={cat.category} style={styles.section}>
          <div style={styles.catHeader}>
            <span style={styles.catIcon}>{cat.icon}</span>
            <span style={styles.catTitle}>{cat.category}</span>
          </div>
          <div style={styles.catGroup}>
            {cat.questions.map((item, i) => {
              const key = `${cat.category}-${i}`
              const isOpen = openItems[key]
              return (
                <div
                  key={key}
                  style={{
                    ...styles.item,
                    ...(isOpen ? styles.itemOpen : {}),
                  }}
                >
                  <button
                    type="button"
                    style={styles.question}
                    onClick={() => toggleItem(key)}
                    aria-expanded={isOpen}
                  >
                    <span style={styles.questionText}>{item.q}</span>
                    <span
                      style={{
                        ...styles.chevron,
                        fontSize: '13px',
                        lineHeight: 1,
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                      aria-hidden="true"
                    >▾</span>
                  </button>
                  {isOpen && <div style={styles.answer}>{item.a}</div>}
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {/* Contact card */}
      <div style={styles.contact}>
        <div style={styles.contactTitle}>Still have a question?</div>
        <div style={styles.contactDesc}>
          We're here to help. Send us a message and we'll get back to you within one business day.
        </div>
        <a href="mailto:hello@getmerq.com" style={styles.contactBtn}>
          hello@getmerq.com
        </a>
      </div>
    </div>
  )
}

// ─── Styles ─────────────────────────────────────────────────

const c = theme.colors
const f = theme.fonts

const styles = {
  page: {
    maxWidth: '760px', margin: '0 auto', width: '100%',
    padding: '24px 32px 80px',
    fontFamily: f.body, color: c.plum,
  },
  pageMobile: {
    width: '100%', padding: '0 16px 80px',
    fontFamily: f.body, color: c.plum,
  },

  header: {
    display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
    marginBottom: '20px', gap: '14px', flexWrap: 'wrap',
  },
  headerMobile: {
    padding: '14px 0',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: '4px' },
  pageTitle: {
    fontFamily: f.display, fontWeight: 600, fontSize: '32px',
    letterSpacing: '-0.025em', color: c.plum, margin: 0,
  },

  searchWrap: {
    display: 'flex', alignItems: 'center', gap: '10px',
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '12px',
    padding: '0 14px',
    marginBottom: '14px',
    boxShadow: theme.shadows.card,
  },
  searchInput: {
    flex: 1, border: 'none', outline: 'none',
    fontFamily: f.body, fontSize: '15px',
    color: c.plum,
    padding: '14px 0',
    background: 'transparent',
    minWidth: 0,
  },
  clearBtn: {
    fontSize: '18px',
    lineHeight: 1,
    background: 'transparent', color: c.inkQuiet,
    border: 'none', cursor: 'pointer',
    padding: '4px',
    display: 'grid', placeItems: 'center',
    borderRadius: '4px',
  },
  resultCount: {
    fontSize: '13px', color: c.inkMuted,
    marginBottom: '14px', padding: '0 4px',
  },

  emptySearch: {
    background: c.white,
    border: `1px dashed ${c.lineStrong}`,
    borderRadius: '14px',
    padding: '40px 28px',
    textAlign: 'center',
    marginBottom: '20px',
  },
  emptyTitle: {
    fontFamily: f.display, fontSize: '17px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.015em',
    marginBottom: '6px',
  },
  emptyText: {
    fontSize: '13.5px', color: c.inkMuted, lineHeight: 1.5,
  },
  emptyLink: {
    color: c.green, fontWeight: 600, textDecoration: 'none',
  },

  section: { marginBottom: '24px' },
  catHeader: {
    display: 'flex', alignItems: 'center', gap: '8px',
    marginBottom: '10px', padding: '0 4px',
  },
  catIcon: { fontSize: '18px' },
  catTitle: {
    fontSize: '12px', fontWeight: 700,
    color: c.inkMuted,
    textTransform: 'uppercase', letterSpacing: '0.06em',
  },

  catGroup: {
    display: 'flex', flexDirection: 'column', gap: '6px',
  },
  item: {
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'border-color 120ms ease',
  },
  itemOpen: {
    borderColor: c.lineStrong,
    boxShadow: '0 1px 3px rgba(30, 16, 64, 0.06)',
  },
  question: {
    width: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '15px 18px',
    background: 'transparent', border: 'none',
    cursor: 'pointer',
    textAlign: 'left', gap: '12px',
    fontFamily: f.body,
  },
  questionText: {
    fontSize: '14.5px', fontWeight: 600,
    color: c.plum, lineHeight: 1.4,
    flex: 1,
  },
  chevron: {
    color: c.inkQuiet, flexShrink: 0,
    transition: 'transform 200ms ease',
  },
  answer: {
    padding: '0 18px 16px',
    fontSize: '14px', color: c.plumSoft,
    lineHeight: 1.7,
    borderTop: `1px solid ${c.line}`,
    paddingTop: '14px',
  },

  contact: {
    background: c.plum,
    borderRadius: '20px',
    padding: '32px',
    textAlign: 'center',
    marginTop: '32px',
    boxShadow: '0 14px 40px rgba(30, 16, 64, 0.18)',
  },
  contactIcon: {
    width: '52px', height: '52px',
    borderRadius: '14px',
    background: 'rgba(250, 247, 242, 0.1)',
    color: c.cream,
    display: 'grid', placeItems: 'center',
    margin: '0 auto 14px',
  },
  contactTitle: {
    fontFamily: f.display, fontSize: '20px', fontWeight: 600,
    color: c.cream, letterSpacing: '-0.02em',
    marginBottom: '8px',
  },
  contactDesc: {
    fontSize: '13.5px', color: 'rgba(250, 247, 242, 0.7)',
    lineHeight: 1.55,
    marginBottom: '20px',
    maxWidth: '360px', margin: '0 auto 20px',
  },
  contactBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '12px 22px',
    background: c.green, color: '#FFFFFF',
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    textDecoration: 'none',
  },
}
